import { useState } from "react";
import { authApi, messageFrom } from "../api";

// API key management + copy-paste integration docs.
// The full key is only ever returned once (at generation), so we hold it in
// local state and show a "copy now" warning; afterwards only the mask is shown.
function ApiKeyPanel({ stats, onChanged }) {
    const [fullKey, setFullKey] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [copied, setCopied] = useState("");

    const origin = typeof window !== "undefined" ? window.location.origin : "https://your-domain";

    const generate = () => {
        const isRegen = stats && stats.has_api_key;
        if (isRegen && !window.confirm("Regenerating will immediately invalidate your current key. Continue?")) return;
        setBusy(true);
        setError("");
        authApi
            .generateApiKey()
            .then((res) => {
                setFullKey(res.data.data.api_key);
                onChanged();
            })
            .catch((err) => setError(messageFrom(err, "Could not generate key")))
            .finally(() => setBusy(false));
    };

    const revoke = () => {
        if (!window.confirm("Revoke your API key? Integrations using it will stop working.")) return;
        setBusy(true);
        setError("");
        authApi
            .revokeApiKey()
            .then(() => {
                setFullKey("");
                onChanged();
            })
            .catch((err) => setError(messageFrom(err, "Could not revoke key")))
            .finally(() => setBusy(false));
    };

    const copy = (text, label) => {
        navigator.clipboard?.writeText(text).then(
            () => {
                setCopied(label);
                setTimeout(() => setCopied(""), 1800);
            },
            () => setError("Could not copy — select and copy manually")
        );
    };

    const curlSample = `curl "${origin}/api/report/carfax?vin=1HGCM82633A004352" \\
  -H "x-api-key: ${fullKey || "YOUR_API_KEY"}"`;

    return (
        <div className="panel">
            <div className="panel__head">
                <h3>API access</h3>
                <p>Use your own key to pull reports from your website or backend. API calls draw from the same report balance.</p>
            </div>

            {error && <div className="banner banner--error">{error}</div>}

            {fullKey && (
                <div className="keybox keybox--new">
                    <div className="keybox__warn">Copy this key now — it won't be shown in full again.</div>
                    <div className="keybox__row">
                        <code className="keybox__value">{fullKey}</code>
                        <button className="btn btn--ghost btn--sm" onClick={() => copy(fullKey, "key")}>
                            {copied === "key" ? "Copied" : "Copy"}
                        </button>
                    </div>
                </div>
            )}

            {!fullKey && stats && stats.has_api_key && (
                <div className="keybox">
                    <div className="keybox__row">
                        <code className="keybox__value">{stats.api_key_masked}</code>
                        <span className="keybox__meta">
                            {stats.api_key_created ? `created ${new Date(stats.api_key_created).toLocaleDateString()}` : ""}
                        </span>
                    </div>
                </div>
            )}

            {!fullKey && stats && !stats.has_api_key && (
                <p className="panel__muted">You don't have an API key yet.</p>
            )}

            <div className="panel__actions">
                <button className="btn btn--primary" onClick={generate} disabled={true}>
                    {busy ? "Working…" : stats && stats.has_api_key ? "Regenerate key" : "Generate API key"}
                </button>
                {stats && stats.has_api_key && (
                    <button className="btn btn--ghost" onClick={revoke} disabled={true}>Revoke</button>
                )}
            </div>

            <div className="docs">
                <div className="docs__head">
                    <h4>Quick start</h4>
                    <button className="btn btn--ghost btn--sm" onClick={() => copy(curlSample, "curl")}>
                        {copied === "curl" ? "Copied" : "Copy"}
                    </button>
                </div>
                <pre className="docs__code">{curlSample}</pre>
                <p className="docs__note">
                    Send your key as <code>x-api-key</code> (or <code>Authorization: Bearer &lt;key&gt;</code>).
                    Response: <code>{"{ html, vin, cached, remaining }"}</code>.
                    Returns <code>403</code> when your report balance is exhausted.
                </p>
            </div>
        </div>
    );
}

export default ApiKeyPanel;
